
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, User, Briefcase, Target } from "lucide-react";
import type { Question } from "@/types/test";

interface MarkschemeContextDisplayProps {
  question?: Question;
  jobContext?: {
    title?: string;
    description?: string;
    keySkills?: string[];
    yearsOfExperience?: string;
  };
}

export const MarkschemeContextDisplay = ({
  question,
  jobContext,
}: MarkschemeContextDisplayProps) => {
  const questionContent = typeof question?.content === 'string' 
    ? JSON.parse(question.content) 
    : question?.content;

  return (
    <Card className="bg-muted/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Available Context for AI Generation
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <h4 className="font-medium mb-2 flex items-center gap-1">
              <Target className="h-3 w-3" />
              Question Details
            </h4>
            <div className="space-y-1 text-muted-foreground">
              <p>• Type: {question?.question_type || "Not specified"}</p>
              <p>• Skill: {question?.skill || "Not specified"}</p>
              <p>• Difficulty: {question?.difficulty || "Not specified"}</p>
              <p>• Marks: {question?.marks || "Not specified"}</p>
              <p>• Question Text: {questionContent?.question ? "Available" : "Not provided"}</p>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium mb-2 flex items-center gap-1">
              <Briefcase className="h-3 w-3" />
              Job Context
            </h4>
            <div className="space-y-1 text-muted-foreground">
              <p>• Title: {jobContext?.title || "Not provided"}</p>
              <p>• Experience: {jobContext?.yearsOfExperience || "Not specified"}</p>
              <p>• Description: {jobContext?.description ? "Available" : "Not provided"}</p>
              <div className="flex items-center gap-1 flex-wrap">
                <span>• Skills:</span>
                {jobContext?.keySkills?.length ? (
                  jobContext.keySkills.slice(0, 3).map((skill, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {skill}
                    </Badge>
                  ))
                ) : (
                  <span>Not provided</span>
                )}
                {jobContext?.keySkills && jobContext.keySkills.length > 3 && (
                  <Badge variant="outline" className="text-xs">
                    +{jobContext.keySkills.length - 3} more
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="pt-2 border-t">
          <p className="text-xs text-muted-foreground">
            <strong>Tip:</strong> The more context you provide in your question details and job information, 
            the more accurate and relevant the AI-generated markscheme will be.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
